import React, { useState, useEffect, useContext } from 'react';
import '../css/loyaltypage.css';
import { authentication, fs } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { LocalizationContext } from '../localization/LocalizationContext';
import { getUserCardNumber, getUserPhoneNumber } from '../utils/userUtils';
import { getWalletBalancesByCardNumber } from '../services/syrveLoyaltySync';
const LoyaltyPage = ({ handleXBarItemChange }) => {
    const { translations } = useContext(LocalizationContext);
    const [step, setStep] = useState(1);
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [message, setMessage] = useState('');
    const [userCard, setUserCard] = useState(null);
    const [coins, setCoins] = useState([]);
    const [hasPendingRequest, setHasPendingRequest] = useState(false);
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(authentication, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await fetchUserCard(currentUser);
                await checkPendingRequests(currentUser.uid);
            }
        });
        return () => unsubscribe();
    }, []);

    const checkPendingRequests = async (uid) => {
        try {
            const requestsRef = collection(fs, "cardRequests");
            const q = query(requestsRef, where("uid", "==", uid), where("status", "==", "pending"));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setHasPendingRequest(true);
                const requestData = querySnapshot.docs[0].data();
                setMessage(translations.loyalty.pendingRequestMessage.replace('{cardNumber}', requestData.cardNumber));
            }
        } catch (error) {
            console.error("Error checking pending requests:", error);
        }
    };

    const fetchUserCard = async (user) => {
        try {
            const userRef = doc(fs, "users", user.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const userData = userSnap.data();
                const cardNum = getUserCardNumber(userData);
                if (cardNum) {
                    setUserCard(cardNum);
                    const phoneNumber = getUserPhoneNumber(userData) || getUserPhoneNumber(user);
                    console.log('Fetching coins for card:', cardNum, 'with phone:', phoneNumber);
                    await fetchCardCoins(cardNum, phoneNumber);
                }
            }
        } catch (error) {
            console.error(translations.loyalty.errorLoadingCard, error);
        }
    };

    const fetchCardCoins = async (cardNum, phoneNumber) => {
        try {
            if (!phoneNumber) {
                console.warn('No phone number available, using Firestore fallback');
            }

            const walletBalances = await getWalletBalancesByCardNumber(cardNum, phoneNumber);

            if (walletBalances) {
                console.log('Successfully fetched wallet balances from Syrve:', walletBalances);
                setCoins(walletBalances);
            } else {
                console.log('Falling back to Firestore coins collection');
                const coinsRef = doc(fs, "coins", cardNum);
                const coinsSnap = await getDoc(coinsRef);
                if (coinsSnap.exists()) {
                    const formattedCoins = Object.fromEntries(
                        Object.entries(coinsSnap.data()).map(([store, value]) => [store, Number(value) || 0])
                    );
                    setCoins(formattedCoins);
                } else {
                    setCoins({});
                }
            }
        } catch (error) {
            console.error(translations.loyalty.errorLoadingPoints, error);
            const coinsRef = doc(fs, "coins", cardNum);
            const coinsSnap = await getDoc(coinsRef);
            if (coinsSnap.exists()) {
                const formattedCoins = Object.fromEntries(
                    Object.entries(coinsSnap.data()).map(([store, value]) => [store, Number(value) || 0])
                );
                setCoins(formattedCoins);
            } else {
                setCoins({});
            }
        }
    };

    const handleNextStep = () => {
        if (!user) {
            setError(translations.loyalty.needToAuthorize);
        } else {
            setStep(2);
            setError('');
        }
    };

    const handleChangeCard = async () => {
        if (!userCard) return;
        
        try {
            // Find and update the old card to remove user connection
            let oldCardRef = doc(fs, "loyaltyCards", userCard);
            let oldCardSnap = await getDoc(oldCardRef);
            
            if (!oldCardSnap.exists()) {
                oldCardRef = doc(fs, "SilverCards", userCard);
                oldCardSnap = await getDoc(oldCardRef);
            }
            
            if (!oldCardSnap.exists()) {
                oldCardRef = doc(fs, "GoldCards", userCard);
                oldCardSnap = await getDoc(oldCardRef);
            }
            
            if (oldCardSnap.exists()) {
                await updateDoc(oldCardRef, {
                    uid: null,
                    email: null,
                    name: null,
                    phone: null,
                    disconnectedAt: new Date()
                });
            }
            
            // Clear user's card
            setUserCard(null);
            setCoins({});
            setStep(2);
            setMessage('');
        } catch (error) {
            console.error("Error changing card:", error);
            setMessage(translations.loyalty.errorChangingCard || translations.loyalty.errorMessage || "Error changing card");
        }
    };

    const handleSubmit = async () => {
        if (!cardNumber) {
            setMessage(translations.loyalty.inputCardNumber);
            return;
        }

        try {
            // Check if card exists in any collection
            let cardRef = doc(fs, "loyaltyCards", cardNumber);
            let cardSnap = await getDoc(cardRef);
            
            // If not in loyaltyCards, check SilverCards
            if (!cardSnap.exists()) {
                cardRef = doc(fs, "SilverCards", cardNumber);
                cardSnap = await getDoc(cardRef);
            }
            
            // If not in SilverCards, check GoldCards
            if (!cardSnap.exists()) {
                cardRef = doc(fs, "GoldCards", cardNumber);
                cardSnap = await getDoc(cardRef);
            }

            // If card exists, check if already connected
            if (cardSnap.exists()) {
                const cardData = cardSnap.data();
                if (cardData.uid) {
                    setMessage(translations.loyalty.cardAlreadyConnected);
                    return;
                }
            }

            // If card doesn't exist anywhere, create it in loyaltyCards collection
            if (!cardSnap.exists()) {
                const newCardRef = doc(fs, "loyaltyCards", cardNumber);
                await setDoc(newCardRef, {
                    uid: user.uid,
                    email: user.email,
                    name: user.displayName || "",
                    phone: user.phoneNumber || "",
                    createdAt: new Date(),
                    status: "active"
                });
            } else {
                // Update existing card with user info
                await updateDoc(cardRef, {
                    uid: user.uid,
                    email: user.email,
                    name: user.displayName || "",
                    phone: user.phoneNumber || "",
                    updatedAt: new Date()
                });
            }

            // Update user document with card number
            const userRef = doc(fs, "users", user.uid);
            await updateDoc(userRef, {
                cardNumber: cardNumber
            });

            // Create initial coins document if it doesn't exist
            const coinsRef = doc(fs, "coins", cardNumber);
            const coinsSnap = await getDoc(coinsRef);
            if (!coinsSnap.exists()) {
                await setDoc(coinsRef, {
                    nadimi: 0,
                    copacabana: 0,
                    aromiitaliani: 0,
                    poshnosh: 0,
                    symposium: 0
                });
            }

            setMessage(translations.loyalty.successMessage || "Card successfully connected!");
            setUserCard(cardNumber);
            const phoneNumber = user.phoneNumber;
            await fetchCardCoins(cardNumber, phoneNumber);
            setStep(1);

        } catch (error) {
            console.error(translations.loyalty.errorCreatingRequest, error);
            setMessage(translations.loyalty.errorCreatingRequest);
        }
    };
    // Корректный расчет значений
    const totalCoins = Object.values(coins).reduce((total, coin) => total + coin, 0);
    const maxCoins = 700;
    const widthPercentage = totalCoins > 0 ? Math.min((totalCoins / maxCoins) * 100, 100) : 0;

    const minCoin = Object.values(coins).length > 0
        ? Math.min(...Object.values(coins).filter(num => num > 0))
        : 0;

    const totalCoins2 = minCoin * 5;
    const widthPercentage2 = totalCoins2 > 0 ? Math.min((totalCoins2 / maxCoins) * 100, 100) : 0;
    const storeLogos = {
        "nadimi": "l1",
        "copacabana": "l2",
        "aromiitaliani": "l3",
        "poshnosh": "l4",
        "symposium": "l5",

    };
    const benefitKeys = [
        'priorityBooking',
        'freeTransfer',
        'tastingNewDishes',
        'birthdayDiscount',
        'vipDelivery',
        'outdoorCatering',
        'yachtParties',
        'billPaymentWithPoints',
        'specialDishDays'
    ];

    return (
        <div className="LoyaltyPage-container">
            <div className={'main_blocks_text'}>{translations.loyalty.title}</div>

            <div className="loyalpage-container">
                <div className="loyalpage-welcome">
                    <h2 className="loyalpage-title">
                        {translations.loyalty.welcome}
                    </h2>
                    <p className="loyalpage-lead">
                        {translations.loyalty.subtitle}
                    </p>
                </div>

                <div className="loyalpage-section">
                    <h3 className="loyalpage-subtitle">{translations.loyalty.howPointsEarned}</h3>
                    <p className="loyalpage-text">
                        {translations.loyalty.everyVisitRewarded}
                    </p>

                    <ul className="loyalpage-list">
                        <li className="loyalpage-list-item">
                            <span className="loyalpage-highlight">{translations.loyalty.oneGelOnePoint}</span>
                        </li>
                        <li className="loyalpage-list-item">
                            {translations.loyalty.pointsCreditedAtRestaurant}
                        </li>
                    </ul>
                </div>

                <div className="loyalpage-section loyalpage-bonus">
                    <h3 className="loyalpage-subtitle">{translations.loyalty.additionalBonus}</h3>
                    <div className="loyalpage-formula">
                        <p className="loyalpage-text">
                            {translations.loyalty.bonusFormula}
                        </p>
                        <div className="loyalpage-note">
                            {translations.loyalty.bonusNote}
                        </div>
                    </div>
                </div>
            </div>

            {userCard ? (
                <>
                    <div className={'user-card-wrapper'}>
                        <div className={'user-card-info'}>
                            <div className={'user-card-title'}>{translations.loyalty.yourCard}: {userCard}</div>

                            {/* Отображение баллов и бонусов */}
                            <div className="loyality1">
                                {Object.entries(coins).map(([store, coin]) => (
                                    <div key={store}
                                         className={`loyality_icon ${storeLogos[store] || "default-icon"}`}>
                                        <div className="loay_num">{coin}</div>

                                    </div>
                                ))}
                            </div>


                            {totalCoins > 0 && (
                                <div className={"loyal-summary"}>
                                    <div className={"loyal-progress"}>
                                        <div className={"loyal-value"}>
                                            {totalCoins}
                                            <div className={"loyal-currency"}>{translations.loyalty.artCoin}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {totalCoins2 > 0 && (
                                <>
                                    <div className={"bonus-info"}>
                                        {minCoin} {translations.loyalty.bonusMultiplier}
                                    </div>

                                    <div className={"bonus-summary"}>
                                        <div className={"bonus-progress"}>
                                            <div className={"bonus-value"}>
                                                {totalCoins2}
                                                <div className={"bonus-currency"}>{translations.loyalty.artBonus}</div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    
                    <div className={'loyalty-button-container'}>
                        <div style={{
                            padding: '10px',
                            textAlign: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: '#666'
                        }}>
                            🔒 {translations.loyalty.cardChangeUnavailable}
                        </div>
                    </div>

                </>
            ) : step === 1 ? (
                <>

                    {error && (
                        <div className="error-loyalty-message" onClick={() => handleXBarItemChange(3)}>
                            <span>{error}</span>
                        </div>
                    )}
                    <div className={'loyalty-button-container'}>
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px'
                        }}>
                            <p style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}>
                                🔒 {translations.loyalty.addLoyaltyCard || 'Add loyalty card'}
                            </p>
                            <p style={{ fontSize: '14px', color: '#888' }}>
                                {translations.loyalty.featureTemporarilyUnavailable}
                            </p>
                        </div>
                    </div>
                </>
            ) : (
                <div className={'enter-card-code-container'}>
                    <div className={'enter-card-title'}>{translations.loyalty.enterCardCode}</div>
                    <input
                        type="text"
                        placeholder={translations.loyalty.cardCodePlaceholder}
                        className={'card-code-input'}
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                    />
                    {message && <div className="message">{message}</div>}
                    <button className={'submit-card-button'} onClick={handleSubmit}>{translations.loyalty.submit}</button>
                    <button className={'back-button'} onClick={() => setStep(1)}>{translations.loyalty.back}</button>
                </div>
            )}
            <div className={'Benefits-main-text'}>{translations.loyalty.memberPrivileges}</div>

            <div className={'benefits-container'}>
            {benefitKeys.map((key, index) => (
                    <div key={index} className="benefits-item">
                        <div className="benefits-title">
                            {translations.loyalty[key]}
                        </div>
                        <div className="benefits-text">{translations.loyalty[key + 'Desc']}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LoyaltyPage;
